import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { v2TeamClient } from "@/utils/axios";
import { getCurrentAuth } from "@/hooks/useAxiosPrivate";
import { APIRoutes } from "@/utils/constants";
import useConfig from "@/hooks/useConfig";

/** One team as the tenant knows it, which is the only place the crests live. */
export type Team = {
  team_id: number;
  /**
   * The same team's id in the search service, and the only thing tying an
   * analytics row to this record. Null on most rows: of 722 teams on the NAHL
   * tenant, 108 carry one.
   */
  team_id_tpe: number | null;
  team_name: string;
  team_short_name: string | null;
  team_logo_url: string | null;
  team_mascot?: string | null;
  team_primary_color?: string | null;
  team_secondary_color?: string | null;
};

/**
 * Every team this tenant has, fetched once and kept.
 *
 * Held for an hour because the list changes on the order of a season, not a
 * session, and several surfaces want it. React Query dedupes by key, so callers
 * can each ask for it without anyone threading it through props.
 */
export function useTeams(enabled = true) {
  const config = useConfig();
  const customer = config?.customer;

  return useQuery<Team[]>({
    queryKey: ["v2", "teams", String(customer ?? "")],
    enabled: Boolean(customer) && enabled,
    retry: 1,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    queryFn: async ({ signal }) => {
      const token = getCurrentAuth()?.AccessToken;
      const res = await v2TeamClient.get<{ data?: Team[] }>(
        APIRoutes.GetTeamsAdmin.replace(":customer", String(customer)),
        { headers: token ? { Authorization: `bearer ${token}` } : {}, signal },
      );
      const body = res.data?.data ?? res.data;
      return Array.isArray(body) ? body : [];
    },
  });
}

/**
 * The same teams keyed by their search-service id, which is how an analytics
 * row refers to them. Teams without one are absent: of 722 on the NAHL tenant
 * only 108 carry a TPE id, and most U16 and U18 squads do not.
 *
 * `enabled` is how a caller that already has what it needs avoids the request
 * entirely. The query is shared, so a caller passing false still sees whatever
 * another caller has already loaded.
 */
export function useTeamsByTpeId(enabled = true): Map<string, Team> {
  const { data } = useTeams(enabled);
  return useMemo(() => {
    const map = new Map<string, Team>();
    for (const team of data ?? []) {
      if (team?.team_id_tpe != null) map.set(String(team.team_id_tpe), team);
    }
    return map;
  }, [data]);
}

/** Comparable form of a team name, so punctuation and case cannot part them. */
const normalise = (name?: string | null) =>
  String(name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/**
 * Whether a tenant team really is the team an analytics row is talking about.
 *
 * TPE ids are assigned per analytics database while this list is served from
 * one shared host, so the same number can mean different teams in different
 * environments: on staging, clip team 8572 is New Jersey here and New Mexico
 * there. The names are the cross-check. The tenant's is usually the shorter
 * ("Louisiana" against the row's "Louisiana Central Drillers"), so either
 * containing the other counts.
 */
export function teamMatchesName(team: Team, rowName?: string | null) {
  const a = normalise(team.team_name);
  const b = normalise(rowName);
  if (!a || !b) return false;
  return a === b || b.startsWith(a) || a.startsWith(b) || b.includes(a);
}
